import { call, put, takeLatest, select } from 'redux-saga/effects';
import { CloudActions, FlowjoCommons } from '@portal/portal-core';

import { selectLicenseRequest } from 'selectors/super-admin';
import { fetchFailure, fetchResume, fetchSuccess } from '../actions';
import type { SuperClientConfig, WrapResult } from '../api';
// `../shared` is untyped JS; `sendActionWithRetries` and `ccTypes` resolve as `any`.
import { sendActionWithRetries, ccTypes } from '../shared';
import { SUPER_ADMIN_CONTINUE_LICENSE_SYNC } from '../types';

// Shape of the count payload returned by `getCostCentersCount`.
interface CostCentersCountData {
  individualCount: number;
  institutionalCount: number;
}

// Action dispatched to the watcher; only `type` is used to route.
interface FetchLicensesAction {
  type: string;
}

// Minimal shape of the paginated cost-centers response consumed below.
interface CostCentersPageResult {
  succeeded: boolean;
  data: {
    costCenterAdmins?: Record<string, unknown>;
    costCenters?: Record<string, unknown>;
  };
}

// Minimal shape of the license request selected by `selectLicenseRequest`.
interface LicenseRequest {
  ccType?: string;
  currentRequest?: number;
  requestsCount?: number;
  syncFailed?: boolean;
}

interface ContinueLicenseSyncAction {
  type: string;
  ccType: string;
}

const licenseCountKeyDict: Record<string, keyof CostCentersCountData> = {
  [ccTypes.individual]: 'individualCount',
  [ccTypes.institutional]: 'institutionalCount',
};

const maxAttempts = 3;
const attemptCount = 1;
const reattemptDelay = 6000;
const requestSpacing = 3000;
// Paginated page fetches can be large; give each attempt more headroom than the
// default 5 s before declaring a timeout so slow networks and slow backend
// processing have a chance to complete.
const paginatedTimeoutMs = 20000;
const normalizePageCount = (value: number | string | undefined): number =>
  Math.max(0, Math.floor(Number(value ?? 0)));

// Fetch license data on load or after change
export function* fetchLicensesByType(
  config: SuperClientConfig,
  ccType: string,
): Generator<unknown, void, unknown> {
  // Hoisted so the catch block can dispatch partial data already accumulated.
  const costCenterAdmins: Record<string, unknown> = {};
  const costCenters: Record<string, unknown> = {};
  let requestsCount = 0;
  let syncedPages = 0;
  let failedAt: 'count' | 'page' = 'count';

  try {
    const limit = ccType === ccTypes.institutional ? 20 : 1000;

    // get license count — retried on failure so a transient timeout doesn't abort the whole sync
    const licenseCountRes = (yield call(
      sendActionWithRetries,
      { ...config, timeout: paginatedTimeoutMs },
      CloudActions.getCostCentersCount(),
      maxAttempts,
      attemptCount,
      reattemptDelay,
      0,
    )) as WrapResult;
    const { data } = licenseCountRes;
    // sendActionWithRetries returns succeeded:true or throws; no failed-path guard needed

    const licenseCount = (data as CostCentersCountData)[licenseCountKeyDict[ccType]];

    requestsCount = Math.ceil(licenseCount / limit);
    failedAt = 'page';

    // start loader at 0.1%
    if (licenseCount > 0) {
      yield put(
        fetchSuccess(
          { costCenterAdmins, costCenters },
          {
            currentRequest: 0.1,
            requestsCount: 1,
            ccType,
            nextRequest: 1,
          },
        ),
      );
    }

    if (requestsCount > 0) {
      syncedPages = normalizePageCount(syncedPages);
      yield put(
        fetchResume({
          currentRequest: syncedPages,
          requestsCount,
          ccType,
          nextRequest: Math.min(syncedPages + 1, requestsCount),
        }),
      );

      for (let i = 0; i < requestsCount; i += 1) {
        const offset = i * limit;
        const ccAction = CloudActions.getCostCentersPaginateVerbose(limit, offset, ccType);

        // `sendActionWithRetries` is untyped JS; the yielded result is `unknown`.
        const res = (yield call(
          sendActionWithRetries,
          { ...config, timeout: paginatedTimeoutMs },
          ccAction,
          maxAttempts,
          attemptCount,
          reattemptDelay,
          requestSpacing,
        )) as CostCentersPageResult;

        if (res.succeeded) {
          Object.assign(costCenterAdmins, res.data.costCenterAdmins || {});
          Object.assign(costCenters, res.data.costCenters || {});
          syncedPages = i + 1;
          yield put(
            fetchSuccess(
              { costCenterAdmins, costCenters },
              {
                currentRequest: syncedPages,
                requestsCount,
                ccType,
                nextRequest: Math.min(syncedPages + 1, requestsCount),
              },
            ),
          );
        } else {
          throw new Error('response failed');
        }
      }
    }
  } catch (error) {
    console.error(`fetch ${ccType} licenses failed --`, error);
    const { Alerts } = FlowjoCommons;
    const errMsg = error instanceof Error ? error.message : String(error);
    const normalizedCurrentRequest = normalizePageCount(syncedPages);
    const nextRequest =
      requestsCount > 0 ? Math.min(normalizedCurrentRequest + 1, requestsCount) : 1;
    yield put(
      fetchFailure(
        {
          currentRequest: normalizedCurrentRequest,
          requestsCount,
          ccType,
          nextRequest,
          failedAt,
        },
        errMsg,
      ),
    );
    yield put(Alerts.AlertActions.setAlert(
      Alerts.Alert.AlertStamp({ id: `${ccType.toUpperCase()}_LICENSES_FETCH_ERROR`, date: null, type: errMsg }),
    ));
  }
}
// @TODO might be unused action
export function* fetchAllLicenses(config: SuperClientConfig): Generator<unknown, void, unknown> {
  // `selectLicenseRequest` is an untyped JS selector; the yielded result is `unknown`.
  const costCenterType = (yield select(selectLicenseRequest)) as LicenseRequest;
  const selectedCcType = costCenterType.ccType ?? ccTypes.institutional;
  yield call(fetchLicensesByType, config, selectedCcType);
}

const fetchLicenses = (
  config: SuperClientConfig,
  action: FetchLicensesAction | ContinueLicenseSyncAction,
): Generator<unknown, void, unknown> | Promise<void> => {
  switch (action.type) {
    case CloudActions.ADMIN_GET_INDIVIDUAL_LICENSES_VERBOSE:
      return fetchLicensesByType(config, ccTypes.individual);
    case CloudActions.ADMIN_GET_INSTITUTIONAL_LICENSES_VERBOSE:
      return fetchLicensesByType(config, ccTypes.institutional);
    case CloudActions.ADMIN_GET_COST_CENTERS_VERBOSE:
      return fetchAllLicenses(config);
    case SUPER_ADMIN_CONTINUE_LICENSE_SYNC: {
      const continueAction = action as ContinueLicenseSyncAction;
      return fetchLicensesByType(config, continueAction.ccType);
    }
    default:
      // Preserves original (unreachable) runtime behavior exactly; `new Promise()`
      // with no executor throws, same as before the TS conversion.
      return new (Promise as unknown as new () => { resolve: () => Promise<void> })().resolve();
  }
};

/* istanbul ignore next reason: no saga watch tests */
export function* watchFetchLicenses(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(
    [
      CloudActions.ADMIN_GET_INDIVIDUAL_LICENSES_VERBOSE,
      CloudActions.ADMIN_GET_INSTITUTIONAL_LICENSES_VERBOSE,
      CloudActions.ADMIN_GET_COST_CENTERS_VERBOSE,
      SUPER_ADMIN_CONTINUE_LICENSE_SYNC,
    ],
    fetchLicenses,
    config,
  );
}
